import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [charCount, setCharCount] = useState(0);
  const MAX_CHARS = 500;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('contact_alt');
    if (honeypot && typeof honeypot === 'string' && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      setCharCount(0);
      return;
    }

    const message = formData.get('message');
    if (message && typeof message === 'string' && message.length > MAX_CHARS) {
      setFormError(`留言内容不能超过 ${MAX_CHARS} 个字符`);
      setFormStatus('error');
      return;
    }

    try {
      const submitAddr = 'https://readdy.ai/api/form/d9qt66ue3oq4jqumtqdg';
      const formPayload = new URLSearchParams();
      for (const [key, val] of formData.entries()) {
        if (key !== 'contact_alt' && typeof val === 'string') {
          formPayload.append(key, val);
        }
      }

      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formPayload.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
        setCharCount(0);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '';
        if (serverMsg.toLowerCase().includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg || '提交失败，请稍后重试');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'http 91.com 官方指南',
    url: 'https://www.91.com',
    description: 'http 91.com 用户指南与访问教程平台，提供最新入口地址、使用教程和常见问题解答。',
    email: 'support@91.com',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'support@91.com',
      availableLanguage: ['Chinese'],
    },
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - http 91.com 官方指南',
    description: '了解 http 91.com 官方指南平台，包括品牌介绍、信誉口碑与联系方式。',
    url: 'https://www.91.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: 'http 91.com 官方指南',
      url: 'https://www.91.com',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        {/* Hero */}
        <section className="w-full px-4 md:px-6 py-16 bg-background-100">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-primary-500 text-sm font-semibold mb-2 tracking-wide uppercase">关于我们</p>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
              http 91.com 官方指南
            </h1>
            <p className="text-foreground-500 text-sm md:text-base leading-relaxed max-w-xl mx-auto">
              我们致力于为 http 91.com 用户提供最全面、最及时的使用指南与访问支持，让每一位用户都能无障碍地享受平台服务。
            </p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="w-full px-4 md:px-6 py-14 md:py-18">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-5">品牌介绍</h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    <strong className="text-foreground-900">http 91.com 官方指南</strong> 是由资深互联网从业者和 http 91.com 平台资深用户共同创建的综合信息平台。我们专注于为 http 91.com 的用户群体提供高质量的使用指引和技术支持。
                  </p>
                  <p>
                    自成立以来，我们始终坚持"用户至上、信息准确、更新及时"的服务理念。我们的团队密切跟踪 http 91.com 平台的每一次更新和变化，确保用户能够在第一时间获取到最新的入口地址、功能变化和使用技巧。
                  </p>
                  <p>
                    无论是刚接触 http 91.com 的新用户，还是希望深入了解平台高级功能的老用户，都可以在这里找到有价值的参考内容。
                  </p>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden h-72 lg:h-80">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20office%20workspace%20with%20computer%20displaying%20abstract%20data%20visualization%2C%20warm%20natural%20lighting%20from%20large%20windows%2C%20clean%20wooden%20desk%20with%20notebook%20and%20coffee%2C%20professional%20yet%20cozy%20atmosphere%2C%20soft%20beige%20and%20cream%20tones%2C%20team%20collaboration%20concept%20with%20subtle%20tech%20elements%2C%20high%20quality%20editorial%20photography&width=800&height=600&seq=about-brand-91com&orientation=landscape"
                  alt="http 91.com 官方指南团队"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="w-full px-4 md:px-6 py-14 md:py-18 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8 text-center">信誉与口碑</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
              <div className="bg-background-50 border border-background-200/60 rounded-xl p-6 text-center">
                <div className="text-3xl font-heading font-bold text-primary-500 mb-2">5000+</div>
                <p className="text-sm text-foreground-500">日活跃读者</p>
              </div>
              <div className="bg-background-50 border border-background-200/60 rounded-xl p-6 text-center">
                <div className="text-3xl font-heading font-bold text-accent-500 mb-2">99.8%</div>
                <p className="text-sm text-foreground-500">信息准确率</p>
              </div>
              <div className="bg-background-50 border border-background-200/60 rounded-xl p-6 text-center">
                <div className="text-3xl font-heading font-bold text-primary-500 mb-2">24h</div>
                <p className="text-sm text-foreground-500">内容更新响应</p>
              </div>
            </div>
            <div className="bg-background-50 border border-background-200/60 rounded-xl p-6">
              <h3 className="text-base font-heading font-semibold text-foreground-900 mb-4">用户评价</h3>
              <div className="flex flex-col gap-4">
                <div className="border-l-2 border-primary-300 pl-4">
                  <p className="text-sm text-foreground-600 leading-relaxed mb-2">
                    "作为新用户，http 91.com 官方指南真的帮了我大忙。从如何访问到如何使用各项功能，教程写得非常清晰。"
                  </p>
                  <p className="text-xs text-foreground-400">—— 来自用户李先生</p>
                </div>
                <div className="border-l-2 border-primary-300 pl-4">
                  <p className="text-sm text-foreground-600 leading-relaxed mb-2">
                    "每次平台入口地址更新，这里的指南都会第一时间同步，让我再也不用担心找不到正确的访问地址了。"
                  </p>
                  <p className="text-xs text-foreground-400">—— 来自用户王女士</p>
                </div>
                <div className="border-l-2 border-primary-300 pl-4">
                  <p className="text-sm text-foreground-600 leading-relaxed mb-2">
                    "非常专业的指南网站，信息更新及时，内容准确详实，已经成为我日常使用的必备参考。"
                  </p>
                  <p className="text-xs text-foreground-400">—— 来自用户张先生</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="w-full px-4 md:px-6 py-14 md:py-18">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-5">联系方式</h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                      <p className="text-sm text-foreground-500">support@91.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-customer-service-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">在线客服</p>
                      <p className="text-sm text-foreground-500">工作日 9:00 - 18:00</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-200 text-secondary-700 flex-shrink-0">
                      <i className="ri-shield-check-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">安全保障</p>
                      <p className="text-sm text-foreground-500">全站数据加密传输，保障用户隐私</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-5">给我们留言</h2>
                <form
                  data-readdy-form="true"
                  onSubmit={handleSubmit}
                  className="bg-background-100 border border-background-200/60 rounded-xl p-5"
                >
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-3.5 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-3.5 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容 <span className="text-foreground-400 font-normal">（{charCount}/{MAX_CHARS}）</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        rows={4}
                        required
                        maxLength={MAX_CHARS}
                        onChange={(e) => setCharCount(e.target.value.length)}
                        className="w-full px-3.5 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                        placeholder="请输入您的留言内容，我们将在1-2个工作日内回复"
                      ></textarea>
                    </div>

                    {/* Honeypot */}
                    <input
                      type="text"
                      name="contact_alt"
                      className="hp-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />

                    <button
                      type="submit"
                      className="w-full px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                    >
                      提交留言
                    </button>

                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600 text-center">留言提交成功！感谢您的反馈，我们会尽快回复。</p>
                    )}
                    {formStatus === 'error' && formError && (
                      <p className="text-sm text-red-500 text-center">{formError}</p>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}